/**
 * Import parsers — format detection + dispatch
 *
 * Delegates to:
 *   - `lib/formatDetection.ts` for format detection
 *   - `lib/universalCSVParser.ts` for CSV parsing
 *   - `parsers/mtga.ts` for MTGA text parsing
 *   - `parsers/mtgcraft.ts` for MtgCraft backup parsing
 *
 * Phase 3: All CSV formats now route through the universal parser.
 * Old per-source parsers (dragonshield, delverlens, moxfield) are removed.
 */
import { detectFormat as unifiedDetectFormat, type DetectedFormat } from '@/lib/formatDetection';
import { parseUniversalCSV } from '@/lib/universalCSVParser';
import type { ImportSource } from '@/types/card';
import type { ImportCard } from '@/types/schemas';

import type { ImportFieldSummary } from '@/lib/universalCSVParser';
import { parseMtgaWithSummary } from './mtga';

export type ParserResult = {
  cards: ImportCard[];
  errors: string[];
  source: ImportSource;
  fieldSummary?: ImportFieldSummary;
};

export type AsyncParserResult = ParserResult & {
  async: true;
};

/**
 * Detect the format of import data (collection context).
 *
 * Returns the ImportSource for backward compatibility with callers
 * that expect `ImportSource | null`. For richer detection results,
 * call `detectFormatFull()`.
 */
export function detectFormat(text: string): ImportSource | null {
  const result = unifiedDetectFormat(text, 'collection');
  if (!result) return null;

  // JSON backups handled separately by mtgcraft parser
  if (result.inputType === 'json') return null;
  return result.source !== 'unknown' ? result.source : null;
}

/**
 * Full format detection returning the rich DetectedFormat object.
 * Used by modals that need hints, category, and confidence data.
 */
export function detectFormatFull(text: string, preferredCategory?: 'collection' | 'deck' | 'list'): DetectedFormat | null {
  return unifiedDetectFormat(text, preferredCategory);
}

/**
 * Parse import data using the appropriate parser.
 *
 * CSV formats dispatch to the universal parser.
 * Text formats dispatch to the MTGA parser.
 * Source parameter enables condition remapping — when the user changes
 * the source dropdown, pass the new source to re-parse with different
 * value maps.
 */
export function parseImport(text: string, format?: ImportSource): ParserResult {
  const detected = format ?? detectFormat(text);

  if (!detected) {
    return {
      cards: [],
      errors: ['Unable to detect import format. Please select a format manually.'],
      source: 'manual',
    };
  }

  try {
    // CSV sources → universal parser
    const csvSources: ImportSource[] = ['dragonshield', 'delverlens', 'delverlens-legacy', 'moxfield'];
    if (csvSources.includes(detected)) {
      const result = parseUniversalCSV(text, detected);
      return {
        cards: result.cards,
        errors: result.cards.length === 0 ? ['No cards found in import data.'] : [],
        source: result.detectedSource,
        fieldSummary: result.fieldSummary,
      };
    }

    // Text sources → MTGA parser
    const result = parseMtgaWithSummary(text);
    return {
      cards: result.cards,
      errors: result.cards.length === 0 ? ['No cards found in import data.'] : [],
      source: detected,
      fieldSummary: result.fieldSummary,
    };
  } catch (error) {
    return {
      cards: [],
      errors: [`Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`],
      source: detected,
    };
  }
}
